import { Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { CaseState } from "@vp/data-access/case";
import { TagsApiService, TagsState } from "@vp/data-access/tags";
import { FormStateProvider } from "@vp/formly/ui-schema-config";
import { Tag, TagTypeFriendlyId } from "@vp/models";
import { Observable, from, of } from "rxjs";
import { map, mergeMap, reduce } from "rxjs/operators";

export interface TagConfig {
  tagTypeFriendlyId: TagTypeFriendlyId;
  accessible?: boolean;
  assignable?: boolean;
}

@Injectable()
export class TagsFormStateProviderService implements FormStateProvider {
  constructor(
    private tagsApiService: TagsApiService,
    private store: Store
  ) {}

  provide(
    state: Record<string, unknown>,
    context?: Record<string, unknown>
  ): Observable<Record<string, unknown>> {
    const stateTags = state.tags as Record<string, TagConfig>;
    if (!stateTags || Object.keys(stateTags).length === 0) {
      return of({});
    }

    const caseId =
      this.store.selectSnapshot(CaseState.current)?.caseId || (context?.caseId as string);
    const allTags = this.store.selectSnapshot(TagsState.tags);

    return from(Object.entries(stateTags)).pipe(
      mergeMap(([key, config]) => {
        const assignable = config.assignable === true;
        const accessible = config.accessible === true;

        if (!assignable && !accessible) {
          return of({
            [key]: this.filterSortActiveTags(allTags, config)
          });
        }

        return this.tagsApiService
          .getTags(
            {
              assignable,
              accessible,
              caseId,
              tagType: [config.tagTypeFriendlyId]
            },
            true,
            2000
          )
          .pipe(
            map(pageResult => ({
              [key]: this.filterSortActiveTags(pageResult.results, config)
            }))
          );
      }),
      reduce((acc, curr) => ({ ...acc, ...curr }), {} as Record<string, unknown>)
    );
  }

  private filterSortActiveTags(tags: Tag[], tagConfig: TagConfig) {
    return tags
      .filter(tag => tag.tagTypeFriendlyId === tagConfig.tagTypeFriendlyId && tag.active)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }
}
